import React, { useState, useMemo } from 'react';
import Button from '../../components/Form/Button';
import { exportToExcel, printAsExcel } from '../../utils/exportToExcel';
import { usePaymentOrders } from '../../hooks/usePaymentOrders';
import { useBranches } from '../../hooks/useBranches';
import { useCurrencies } from '../../hooks/useCurrencies';
import { resolveCurrencyCode, resolveEffectiveRate } from '../../utils/currencyUtils';
import { PageHeader, StatsCards, FilterBar, type FilterConfig, type StatCard } from '../../components/Common';

const PaymentOrdersReport: React.FC = () => {
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [branchFilter, setBranchFilter] = useState<string>('all');
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');
  const [amountFrom, setAmountFrom] = useState<string>('');
  const [amountTo, setAmountTo] = useState<string>('');

  const { data: paymentOrdersData = [] } = usePaymentOrders();
  const { data: branchesData = [] } = useBranches();
  const { data: currenciesData } = useCurrencies();

  const filteredPaymentOrders = useMemo(() => {
    return paymentOrdersData.filter(order => {
      const matchesSearch = !searchTerm || (
        order.id.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (order.comments?.toLowerCase().includes(searchTerm.toLowerCase()) || '') ||
        order.requestedBy.toLowerCase().includes(searchTerm.toLowerCase())
      );

      const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
      const matchesBranch = branchFilter === 'all' || order.providerId === branchFilter;

      const orderDate = new Date(order.createdAt);
      const matchesDateFrom = !dateFrom || orderDate >= new Date(dateFrom);
      const matchesDateTo = !dateTo || orderDate <= new Date(dateTo);

      const totalAmount = order.total;
      const matchesAmountFrom = !amountFrom || totalAmount >= parseFloat(amountFrom);
      const matchesAmountTo = !amountTo || totalAmount <= parseFloat(amountTo);

      return matchesSearch && matchesStatus && matchesBranch && 
             matchesDateFrom && matchesDateTo && matchesAmountFrom && matchesAmountTo;
    });
  }, [searchTerm, statusFilter, branchFilter, dateFrom, dateTo, amountFrom, amountTo, paymentOrdersData]);

  // Each order is reported in its own currency, with an EGP equivalent
  const orderCurrency = (order: any) => {
    const code = resolveCurrencyCode(order.currency, currenciesData as any[]) || 'EGP';
    const rate = resolveEffectiveRate(order.exchangeRate, order.currency, currenciesData as any[], 50);
    const egpEquivalent = code === 'EGP' ? (order.total || 0) : (order.total || 0) * rate;
    return { code, rate, egpEquivalent };
  };

  const buildExportData = () => filteredPaymentOrders.map(order => {
    const provider = branchesData.find(b => b.id === order.providerId);
    const { code, egpEquivalent } = orderCurrency(order);
    return {
      'Order Number': order.id,
      'Requested By': order.requestedBy,
      'Vessel/Headquarters': provider?.name || 'غير محدد',
      'Date': order.createdAt,
      'Amount': order.total,
      'Currency': code,
      'Amount (EGP Equivalent)': egpEquivalent,
      'Status': order.status,
      'Comments': order.comments || ''
    };
  });

  const handleExport = () => exportToExcel(buildExportData(), `payment_orders_report_${new Date().toISOString().split('T')[0]}`);
  const handlePrint = () => printAsExcel(buildExportData(), 'Payment Orders Report');

  const handleClearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setBranchFilter('all');
    setDateFrom('');
    setDateTo('');
    setAmountFrom('');
    setAmountTo('');
  };

  // One totals bucket per currency actually used by the filtered orders
  const totals = useMemo(() => {
    const byCurrency: Record<string, { total: number; count: number }> = {};
    let egpEquivalent = 0;
    filteredPaymentOrders.forEach(order => {
      const { code, egpEquivalent: eq } = orderCurrency(order);
      if (!byCurrency[code]) byCurrency[code] = { total: 0, count: 0 };
      byCurrency[code].total += order.total || 0;
      byCurrency[code].count += 1;
      egpEquivalent += eq;
    });
    return { byCurrency, egpEquivalent };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredPaymentOrders, currenciesData]);

  const stats: StatCard[] = [
    { label: 'إجمالي الأوامر', value: filteredPaymentOrders.length, color: 'blue' },
    ...Object.entries(totals.byCurrency).map(([code, t]): StatCard => ({
      label: `إجمالي المبلغ (${code})`,
      value: `${t.total.toLocaleString()} ${code}`,
      subtitle: `${t.count} أمر`,
      color: 'yellow',
    })),
    { label: 'الإجمالي معادل بالجنيه', value: `${totals.egpEquivalent.toLocaleString()} EGP`, color: 'purple' },
  ];

  const filters: FilterConfig[] = [
    { key: 'status', label: 'الحالة', type: 'select', value: statusFilter, onChange: setStatusFilter, options: [
      { value: 'all', label: 'جميع الحالات' }, { value: 'pending', label: 'قيد الانتظار' },
      { value: 'approved', label: 'معتمد' }, { value: 'paid', label: 'مدفوع' }, { value: 'cancelled', label: 'ملغي' },
    ]},
    { key: 'branch', label: 'الفرع', type: 'select', value: branchFilter, onChange: setBranchFilter, options: [
      { value: 'all', label: 'جميع الفروع' },
      ...branchesData.map(branch => ({ value: branch.documentId || branch.id, label: branch.name })),
    ]},
    { key: 'dateFrom', label: 'من تاريخ', type: 'date', value: dateFrom, onChange: setDateFrom },
    { key: 'dateTo', label: 'إلى تاريخ', type: 'date', value: dateTo, onChange: setDateTo },
    { key: 'amountFrom', label: 'من مبلغ', type: 'number', value: amountFrom, onChange: setAmountFrom, placeholder: '0' },
    { key: 'amountTo', label: 'إلى مبلغ', type: 'number', value: amountTo, onChange: setAmountTo, placeholder: '999999' },
  ];

  const statusMap: Record<string, { label: string; cls: string }> = {
    paid: { label: 'مدفوع', cls: 'bg-green-100 text-green-800' },
    approved: { label: 'معتمد', cls: 'bg-blue-100 text-blue-800' },
    pending: { label: 'قيد الانتظار', cls: 'bg-yellow-100 text-yellow-800' },
    cancelled: { label: 'ملغي', cls: 'bg-red-100 text-red-800' },
  };

  return (
    <div className="space-y-6" dir="rtl">
      <PageHeader title="تقرير أوامر الدفع" description="عرض وتحليل جميع أوامر الدفع مع إمكانية التصفية والتصدير" />
      <StatsCards stats={stats} />
      <FilterBar
        searchTerm={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="البحث في أوامر الدفع..."
        filters={filters} onClearFilters={handleClearFilters}
        totalCount={paymentOrdersData.length} filteredCount={filteredPaymentOrders.length} entityName="تصفية التقرير"
        onExport={handleExport} onPrint={handlePrint}
      />

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg leading-6 font-medium text-gray-900">
              نتائج التقرير ({filteredPaymentOrders.length} أمر)
            </h3>
            <Button onClick={handleExport} variant="secondary" size="sm">تصدير CSV</Button>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  {['رقم الأمر', 'المستفيد', 'الفرع', 'تاريخ الإنشاء', 'المبلغ', 'المعادل بالجنيه', 'الحالة', 'الوصف'].map(h => (
                    <th key={h} className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredPaymentOrders.map((order) => {
                  const provider = branchesData.find(b => b.id === order.providerId);
                  const { code, egpEquivalent } = orderCurrency(order);
                  const st = statusMap[order.status] || { label: order.status, cls: 'bg-gray-100 text-gray-800' };
                  return (
                    <tr key={order.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{order.id}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{order.requestedBy}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{provider?.name || 'غير محدد'}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{new Date(order.createdAt).toLocaleDateString('ar-EG')}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 text-right">{(order.total || 0).toLocaleString()} {code}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">{egpEquivalent.toLocaleString()} جنيه</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 text-right">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${st.cls}`}>{st.label}</span>
                      </td>
                      <td className="px-6 py-4 text-sm text-gray-500 max-w-xs truncate text-right">{order.comments || 'غير محدد'}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {filteredPaymentOrders.length === 0 && (
            <div className="text-center py-8"><p className="text-gray-500">لا توجد أوامر دفع تطابق المعايير المحددة</p></div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PaymentOrdersReport;
